import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, RefreshCw, Gift, Tag, Upload } from "lucide-react";
import { PricingSection } from "@/components/landing/PricingSection";

type FaqItem = {
  questionKey: string;
  answerKey: string;
  Icon: typeof RefreshCw;
};

const faqItems: FaqItem[] = [
  { questionKey: "pricingPage.faq1Question", answerKey: "pricingPage.faq1Answer", Icon: RefreshCw },
  { questionKey: "pricingPage.faq2Question", answerKey: "pricingPage.faq2Answer", Icon: Gift },
  { questionKey: "pricingPage.faq4Question", answerKey: "pricingPage.faq4Answer", Icon: Tag },
  { questionKey: "pricingPage.faq5Question", answerKey: "pricingPage.faq5Answer", Icon: Upload },
];

export function PricingFAQSection() {
  const { t } = useTranslation();
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <>
      <PricingSection />

      <section className="py-20 bg-gradient-to-b from-gray-50 via-white to-gray-50">
        <div className="container mx-auto px-4 max-w-5xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-4xl md:text-5xl font-heading font-extrabold text-landing-text mb-4">
              {t("pricingPage.faqTitle")}
            </h2>
            <p className="text-base md:text-lg text-landing-text/70">
              {t("pricingPage.faqSubtitle")}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 items-start">
            {faqItems.map((faq, index) => {
              const isOpen = openIndex === index;
              const Icon = faq.Icon;
              return (
                <div
                  key={faq.questionKey}
                  className={`bg-white rounded-2xl border border-landing-border/60 shadow-sm transition-all duration-300 hover:shadow-md ${
                    isOpen ? "shadow-md border-landing-accent/40" : ""
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => setOpenIndex(isOpen ? null : index)}
                    aria-expanded={isOpen}
                    className="w-full flex items-center gap-4 px-5 py-5 text-left"
                  >
                    <span className="flex-shrink-0 w-11 h-11 rounded-full bg-teal-100/70 text-teal-600 flex items-center justify-center">
                      <Icon className="w-5 h-5" strokeWidth={2.25} />
                    </span>
                    <span className="flex-1 text-base font-semibold text-landing-text leading-snug">
                      {t(faq.questionKey)}
                    </span>
                    <Plus
                      className={`w-5 h-5 flex-shrink-0 text-landing-accent transition-transform duration-300 ${
                        isOpen ? "rotate-45" : ""
                      }`}
                    />
                  </button>

                  <AnimatePresence initial={false}>
                    {isOpen && (
                      <motion.div
                        key="content"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: [0.4, 0, 0.2, 1] }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pb-5 pl-[4.75rem] text-sm text-landing-text/80 leading-relaxed">
                          {t(faq.answerKey)}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
